import React, { useState, useEffect } from 'react';

const WhyChooseMASTIVIBES = () => {
  const [animatedTitle, setAnimatedTitle] = useState('');
  const [activeReason, setActiveReason] = useState(0);
  
  const fullTitle = "Why Users Love MASTIVIBES";
  
  const reasons = [
    {
      icon: '👥',
      title: 'Unlimited Group Creation',
      description: 'Create public or private groups with custom fields and personalized settings for any community or interest.',
      gradient: 'from-pink-500 to-purple-500',
      features: ['Custom Group Fields', 'Role Management', 'Privacy Controls']
    },
    {
      icon: '📹',
      title: 'High-Quality Video Calls',
      description: 'HD group video calls with up to 100 participants, screen sharing, filters, and recording options.',
      gradient: 'from-cyan-500 to-blue-500',
      features: ['100+ Participants', 'Screen Sharing', 'HD Quality']
    },
    {
      icon: '🎨',
      title: 'Complete Customization',
      description: 'Design unique profiles with custom fields, themes, and personalized layouts that reflect your style.',
      gradient: 'from-yellow-500 to-orange-500',
      features: ['Custom Profile Fields', 'Themes & Layouts', 'Personal Branding']
    },
    {
      icon: '💬',
      title: 'Real-time Communication',
      description: 'Instant messaging, voice rooms, and collaborative tools that keep everyone connected and engaged.',
      gradient: 'from-green-500 to-teal-500',
      features: ['Voice Rooms', 'Instant Messaging', 'File Sharing']
    }
  ];

  const testimonials = [
    {
      name: "Alex Johnson",
      role: "Content Creator, 50K Followers",
      text: "MASTIVIBES changed how I connect with my audience. The group video calls and custom fields are game-changers!",
      avatar: "🎬"
    },
    {
      name: "Samira Khan",
      role: "Community Manager",
      text: "Managing multiple communities has never been easier. The customization options are exactly what we needed.",
      avatar: "👩‍💼"
    },
    {
      name: "Marcus Chen",
      role: "Remote Team Lead",
      text: "Our team of 75 uses MASTIVIBES daily. The video call quality and group features are superior to any other platform.",
      avatar: "👨‍💻"
    }
  ];

  // Social platform floating icons
  const socialFloatingIcons = [
    { icon: '🎥', size: 'w-12 h-12', position: 'top-20 left-20', delay: 'delay-0' },
    { icon: '📞', size: 'w-10 h-10', position: 'top-32 right-32', delay: 'delay-1000' },
    { icon: '👥', size: 'w-11 h-11', position: 'bottom-20 left-20', delay: 'delay-2000' },
    { icon: '🎭', size: 'w-9 h-9', position: 'bottom-10 right-10', delay: 'delay-1500' },
    { icon: '🎵', size: 'w-10 h-10', position: 'top-1/3 left-1/4', delay: 'delay-500' },
    { icon: '💬', size: 'w-8 h-8', position: 'bottom-1/3 right-1/4', delay: 'delay-2500' },
    { icon: '✨', size: 'w-11 h-11', position: 'top-1/4 right-1/3', delay: 'delay-3000' },
    { icon: '🎊', size: 'w-9 h-9', position: 'bottom-1/4 left-1/3', delay: 'delay-3500' }
  ];

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullTitle.length) {
        setAnimatedTitle(fullTitle.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 40);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const reasonInterval = setInterval(() => {
      setActiveReason((prev) => (prev + 1) % reasons.length);
    }, 5000);

    return () => clearInterval(reasonInterval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-700 via-pink-600 to-orange-500 pt-20 overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-64 h-64 bg-yellow-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-orange-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div 
            key={i}
            className="absolute w-2 h-2 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.2}s`,
              opacity: 0.7
            }}
          />
        ))}
      </div>

      {/* Floating Social Icons */}
      {socialFloatingIcons.map((item, index) => (
        <div
          key={index}
          className={`absolute ${item.position} ${item.size} bg-white/20 backdrop-blur-lg rounded-2xl border-2 border-white/30 flex items-center justify-center text-2xl animate-float-slow ${item.delay} z-20 shadow-2xl hover:scale-125 transition-transform duration-300 cursor-pointer`}
          style={{
            animationDuration: `${6 + index}s`
          }}
        >
          {item.icon}
        </div>
      ))}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Animated Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30 mb-8 shadow-lg">
            <span className="w-3 h-3 bg-yellow-400 rounded-full animate-ping shadow-md"></span>
            <span className="text-white font-bold">Loved by 500,000+ Active Users Worldwide</span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            <span className="bg-gradient-to-r from-yellow-300 via-pink-400 to-orange-400 bg-clip-text text-transparent animate-gradient-flow">
              {animatedTitle}
            </span>
            <span className="animate-pulse text-yellow-300">|</span>
          </h2>
          
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed font-medium">
            Join hundreds of thousands of creators, communities, and connectors who choose MASTIVIBES 
            for the ultimate social experience with unlimited customization and real-time interaction.
          </p>
        </div>

        {/* Main Reasons Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className={`group relative transition-all duration-500 ${
                index === activeReason ? 'scale-105' : 'scale-100'
              }`}
            >
              <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl transform transition-all duration-500 hover:scale-105 h-full hover:shadow-pink-500/25">
                {/* Icon and Header */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${reason.gradient} rounded-2xl flex items-center justify-center text-2xl text-white shadow-2xl transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 ${index === activeReason ? 'animate-pulse' : ''}`}>
                    {reason.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white group-hover:text-yellow-300 transition-colors duration-300">
                      {reason.title}
                    </h3>
                    <div className="flex space-x-2 mt-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse shadow-md"></div>
                      <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse delay-100 shadow-md"></div>
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse delay-200 shadow-md"></div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-white/80 mb-6 leading-relaxed text-lg font-medium">
                  {reason.description}
                </p>

                {/* Features List */}
                <div className="space-y-3">
                  {reason.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-3 group transform hover:translate-x-2 transition-transform duration-300">
                      <div className="w-7 h-7 bg-gradient-to-r from-yellow-400 to-pink-400 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                        <span className="text-white text-sm font-bold">✦</span>
                      </div>
                      <span className="text-white font-semibold group-hover:text-yellow-300 transition-colors duration-300">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Active State Glow */}
              {index === activeReason && (
                <div className={`absolute inset-0 bg-gradient-to-r ${reason.gradient} rounded-3xl blur-xl opacity-30 animate-pulse -z-10 shadow-2xl`}></div>
              )}
            </div>
          ))}
        </div>

        {/* Platform Comparison Section */}
        <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              See the{' '}
              <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                MASTIVIBES Advantage
              </span>
            </h3>
            <p className="text-white/90 text-lg font-medium">
              Compare how we provide better social experiences than traditional platforms
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                metric: 'Group Participants',
                mastivibes: '100+ users',
                traditional: '20-30 users',
                improvement: '4x more capacity',
                color: 'text-yellow-300'
              },
              {
                metric: 'Customization',
                mastivibes: 'Unlimited fields',
                traditional: 'Limited options',
                improvement: 'Complete control',
                color: 'text-yellow-300'
              },
              {
                metric: 'Real-time Features',
                mastivibes: 'Multi-feature',
                traditional: 'Basic chat only',
                improvement: 'Advanced tools',
                color: 'text-yellow-300'
              }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-white/10 rounded-2xl border-2 border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105 shadow-lg">
                <div className="text-2xl font-bold text-white mb-4">{item.metric}</div>
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-yellow-500/20 to-pink-500/20 p-4 rounded-xl border-2 border-yellow-400/30">
                    <div className="text-yellow-300 font-bold text-lg">MASTIVIBES</div>
                    <div className="text-white text-xl font-bold">{item.mastivibes}</div>
                  </div>
                  <div className="bg-white/10 p-4 rounded-xl border border-white/20">
                    <div className="text-white/70">Other Platforms</div>
                    <div className="text-white">{item.traditional}</div>
                  </div>
                  <div className={`text-sm font-bold ${item.color} bg-white/10 rounded-full px-3 py-1 inline-block`}>
                    ⚡ {item.improvement}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white/15 backdrop-blur-lg rounded-2xl p-6 border-2 border-white/25 hover:bg-white/20 transition-all duration-300 hover:scale-105 group shadow-xl"
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-2xl flex items-center justify-center text-2xl transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  {testimonial.avatar}
                </div>
                <div>
                  <div className="text-white font-bold text-lg">{testimonial.name}</div>
                  <div className="text-yellow-300 text-sm font-semibold">{testimonial.role}</div>
                </div>
              </div>
              <p className="text-white/80 italic leading-relaxed font-medium">
                "{testimonial.text}"
              </p>
              <div className="flex space-x-1 mt-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} className="text-yellow-400 text-lg">⭐</span>
                ))}
              </div>
              <div className="mt-4 text-xs text-white/60">
                Verified User • 2 weeks ago
              </div>
            </div>
          ))}
        </div>

        {/* User Satisfaction Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          {[
            { percentage: '98%', label: 'User Satisfaction', icon: '😊', color: 'from-yellow-400 to-orange-400' },
            { percentage: '95%', label: 'Platform Uptime', icon: '⚡', color: 'from-pink-400 to-purple-400' },
            { percentage: '4.9', label: 'App Store Rating', icon: '⭐', color: 'from-cyan-400 to-blue-400' },
            { percentage: '92%', label: 'Would Recommend', icon: '💬', color: 'from-green-400 to-teal-400' }
          ].map((stat, index) => (
            <div 
              key={index}
              className="text-center p-6 bg-white/10 backdrop-blur-lg rounded-2xl border-2 border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105 transform hover:-translate-y-1 group shadow-lg"
            >
              <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center text-2xl text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                {stat.icon}
              </div>
              <div className="text-4xl font-black bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                {stat.percentage}
              </div>
              <div className="text-white font-bold mt-2">
                {stat.label}
              </div>
              <div className="mt-3 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>

        {/* Final CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-yellow-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-8 border-2 border-yellow-400/30 shadow-2xl">
            <h3 className="text-3xl font-bold text-white mb-4">
              Ready to Revolutionize Your Social Experience?
            </h3>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto font-medium">
              Join creators, communities, and connectors who are already building amazing experiences on MASTIVIBES.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-xl blur opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                <div className="relative bg-white/20 backdrop-blur-lg text-white font-bold py-4 px-10 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center transform hover:-translate-y-1 border-2 border-white/30 shadow-2xl">
                  <span className="mr-3 text-xl">🚀</span>
                  Start Your MASTIVIBES Journey - FREE!
                  <span className="ml-3 text-xl group-hover:translate-x-2 transition-transform">→</span>
                </div>
              </button>
              <button className="border-3 border-yellow-400/50 text-yellow-300 hover:bg-yellow-400/20 font-bold py-4 px-8 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center backdrop-blur-lg shadow-lg">
                <span className="mr-3 text-xl">📱</span>
                Download Mobile App
                <span className="ml-2 text-xs px-2 py-1 bg-yellow-500/30 rounded-full">BETA</span>
              </button>
            </div>
            <div className="mt-6 text-white/70 text-sm">
              No credit card required • Free forever plan available
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes float-slow {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          33% { 
            transform: translateY(-20px) rotate(5deg) scale(1.1); 
          }
          66% { 
            transform: translateY(10px) rotate(-3deg) scale(0.95); 
          }
        }
        @keyframes gradient-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        .animate-gradient-flow {
          background-size: 200% auto;
          animation: gradient-flow 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default WhyChooseMASTIVIBES;